/**
 * slotChangeFormatter — les morceaux de phrase de « Modifier mes créneaux », écrits une fois : un
 * cours se nomme pareil dans le rappel d'une étape, dans la question de confirmation et dans
 * l'accusé (« Maths du jeudi 18h00, avec Camille »). Les lignes des récapitulatifs (« Confirmer
 * votre choix ? », « C'est fait », « Demande envoyée ») se composent ici, une pastille et un
 * fragment en gras chacune. Et les lectures des choix que la machine (`useSlotChangeFlow`) et le
 * gabarit (`<SlotChangeFlow>`) partagent : la place choisie d'un cours, ce qui ouvre le bouton
 * d'une étape, et les horaires que l'offre de chaque tuteur portera.
 *
 * Les heures que le serveur écrit (« lundi 18h ») restent les siennes ; celles que l'écran dérive
 * d'un créneau (« jeudi 18h00 ») passent par `weeklySlotFormatter`.
 */
import {
    type UIParentRequest,
    type UIRecapLine,
    type UISlotChangeCourse,
    type UISlotChangeFallback,
    type UISlotChangeFallbackChoice,
    type UISlotChangeGrid,
    type UISlotChangeOptions,
    type UISlotChangeOutcome,
    type UISlotChangePending,
    type UISlotChangeSeat,
    type UISlotChangeTutor,
    type UISubject,
    type UITutor,
} from "../types"
import { elide } from "./childFormatter"
import { formatDayLabel, formatDayMonth } from "./dateFormatter"
import { capitalizeFirst, countLabel, joinFrench } from "./textFormatter"
import { formatWeeklySlot, weeklySlotParts } from "./weeklySlotFormatter"

/** « Maths », « Maths et Physique » — vide pour un cours sans matière. */
export function subjectsLabel(subjects: Pick<UISubject, "name">[]): string {
    return joinFrench(subjects.map((subject) => subject.name).filter((name) => name !== ""))
}

/**
 * Un cours nommé — « Maths du jeudi 18h00 », « Le cours du jeudi 18h00 » sans matière.
 * `withTutor` ajoute « , avec Camille » ; `inSentence` l'écrit au milieu d'une phrase (« maths du
 * jeudi 18h00 », « le cours du jeudi 18h00 »).
 */
export function slotChangeCourseLabel(
    course: Pick<UISlotChangeCourse, "subjects" | "label" | "dayTime" | "tutor">,
    options: { withTutor?: boolean; inSentence?: boolean } = {}
): string {
    const { withTutor = false, inSentence = false } = options
    const subjects = subjectsLabel(course.subjects)
    const slot = formatWeeklySlot(course.label, course.dayTime, "sentence")
    const head =
        subjects !== ""
            ? `${inSentence ? subjects.toLowerCase() : subjects} du ${slot}`
            : `${inSentence ? "le" : "Le"} cours du ${slot}`
    return withTutor && course.tutor?.firstname ? `${head}, avec ${course.tutor.firstname}` : head
}

/**
 * Les créneaux d'un tuteur qui dépendent de son repli : ceux qu'il garde (`keptLiveSessionIds`) et
 * ceux qui changent avec lui parmi les cours choisis — ce que « ses deux créneaux » compte.
 */
export function fallbackSlotsCount(
    fallback: Pick<UISlotChangeFallback, "tutor" | "keptLiveSessionIds">,
    movingCourses: Pick<UISlotChangeCourse, "tutor">[]
): number {
    const moving = movingCourses.filter((course) => String(course.tutor?.id) === String(fallback.tutor.id)).length
    return fallback.keptLiveSessionIds.length + moving
}

/** Les petits nombres s'écrivent en toutes lettres dans une phrase. */
const NUMBER_WORDS: Record<number, string> = { 1: "un", 2: "deux", 3: "trois", 4: "quatre", 5: "cinq" }

/** « deux », « trois »… ; un chiffre au-delà de cinq. */
export function numberInWords(count: number): string {
    return NUMBER_WORDS[count] ?? String(count)
}

/** « de maths », « d'anglais » — la matière en minuscules, élidée devant une voyelle ; vide sans matière. */
function ofSubjects(fallback: Pick<UISlotChangeFallback, "subjects">): string {
    const subjects = subjectsLabel(fallback.subjects).toLowerCase()
    return subjects === "" ? "" : ` ${elide(subjects)}`
}

/** « Prendre un autre tuteur pour ses deux créneaux de maths ». */
export function otherTutorLabel(fallback: UISlotChangeFallback, courses: Pick<UISlotChangeCourse, "tutor">[]): string {
    const count = fallbackSlotsCount(fallback, courses)
    const slots = count > 1 ? `ses ${numberInWords(count)} créneaux` : "son créneau"
    return `Prendre un autre tuteur pour ${slots}${ofSubjects(fallback)}`
}

/** « Garder Camille le lundi 17h, et prendre un autre tuteur pour le nouvel horaire ». */
export function keepTutorLabel(fallback: Pick<UISlotChangeFallback, "tutor" | "keptLabel">): string {
    const name = fallback.tutor.firstname || "son tuteur"
    const kept = fallback.keptLabel !== "" ? ` ${fallback.keptLabel}` : ""
    return `Garder ${name}${kept}, et prendre un autre tuteur pour le nouvel horaire`
}

/** « Si elle ne peut pas », « S'il ne peut pas » — le prénom quand on ne sait pas le genre. */
export function ifTutorCannot(tutor: Pick<UITutor, "firstname" | "gender">): string {
    if (tutor.gender === "femme") return "Si elle ne peut pas"
    if (tutor.gender === "homme") return "S'il ne peut pas"
    return `Si ${tutor.firstname || "son tuteur"} ne peut pas`
}

/**
 * Une matière chez un tuteur — le niveau où le serveur demande le repli. Un serveur d'avant
 * `fallbackKey` seulement : la clé du serveur nomme l'objectif d'un cours sans matière, celle-ci non.
 */
function groupKey(tutor: Pick<UITutor, "id">, subjects: Pick<UISubject, "id">[]): string {
    return `${tutor.id}|${subjects
        .map((subject) => String(subject.id))
        .sort()
        .join(",")}`
}

/**
 * Un tuteur dont les cours d'une matière changent sans qu'il en garde aucun dans cette matière :
 * rien à lui demander de garder. `alsoAsked` : on lui demande déjà le repli d'une autre matière.
 */
export type SlotChangeTutorWithoutFallback = {
    key: string
    tutor: UITutor
    subjects: UISubject[]
    count: number
    alsoAsked: boolean
}

/**
 * Les cours choisis que l'étape du repli ne couvre pas : ceux d'un tuteur qui n'en garde aucun
 * dans la matière. Si ce tuteur ne peut pas, un autre est cherché — rien à choisir, on le dit.
 * Un cours se range sous la clé que le serveur lui donne (`fallbackKey`, celle de `fallback.key`).
 */
export function tutorsWithoutFallback(
    fallbacks: Pick<UISlotChangeFallback, "key" | "tutor" | "subjects">[],
    courses: Pick<UISlotChangeCourse, "tutor" | "subjects" | "fallbackKey">[]
): SlotChangeTutorWithoutFallback[] {
    const askedKeys = new Set(fallbacks.map((fallback) => fallback.key))
    const askedGroups = new Set(fallbacks.map((fallback) => groupKey(fallback.tutor, fallback.subjects)))
    const rows = new Map<string, SlotChangeTutorWithoutFallback>()
    for (const course of courses) {
        if (course.tutor == null) continue
        const tutor = course.tutor
        const asked =
            course.fallbackKey != null
                ? askedKeys.has(course.fallbackKey)
                : askedGroups.has(groupKey(tutor, course.subjects))
        if (asked) continue
        const key = course.fallbackKey ?? groupKey(tutor, course.subjects)
        const row = rows.get(key)
        if (row != null) row.count += 1
        else
            rows.set(key, {
                key,
                tutor,
                subjects: course.subjects,
                count: 1,
                alsoAsked: fallbacks.some((fallback) => String(fallback.tutor.id) === String(tutor.id)),
            })
    }
    return [...rows.values()]
}

/**
 * « Ceux de Yanis aussi. » — et « Ceux de Camille en français aussi. » quand on lui demande déjà le
 * repli d'une autre matière : la phrase dit de quels horaires elle parle.
 */
export function theirsTooLabel(row: SlotChangeTutorWithoutFallback): string {
    const name = row.tutor.firstname || "son tuteur"
    const subjects = subjectsLabel(row.subjects).toLowerCase()
    return `Ceux de ${name}${row.alsoAsked && subjects !== "" ? ` en ${subjects}` : ""} aussi.`
}

/** « , en maths » — la matière d'un repli quand l'étape en compte plusieurs ; vide sinon. */
export function fallbackSubjectSuffix(fallback: Pick<UISlotChangeFallback, "subjects">, several: boolean): string {
    const subjects = subjectsLabel(fallback.subjects).toLowerCase()
    return several && subjects !== "" ? `, en ${subjects}` : ""
}

/** « pour son cours de français », « pour ses deux cours de maths ». */
export function anotherTutorFor(row: SlotChangeTutorWithoutFallback): string {
    const courses = row.count > 1 ? `ses ${numberInWords(row.count)} cours` : "son cours"
    return `nous cherchons un autre tuteur pour ${courses}${ofSubjects(row)}.`
}

/** La place choisie pour ce cours, si elle est parmi celles offertes. */
export function chosenSeat(
    seats: UISlotChangeSeat[],
    choices: Record<number, number>,
    courseLiveSessionId: number
): UISlotChangeSeat | undefined {
    return seats.find(
        (seat) =>
            seat.courseLiveSessionId === courseLiveSessionId && seat.liveSessionId === choices[courseLiveSessionId]
    )
}

/** Chaque cours choisi a-t-il sa place ? Le serveur en veut une pour CHACUN sur cette voie. */
export function seatsComplete(choice: { selectedCourseIds: number[]; seatChoices: Record<number, number> }): boolean {
    return choice.selectedCourseIds.length > 0 && choice.selectedCourseIds.every((id) => choice.seatChoices[id] != null)
}

/**
 * Les cases de la grille où tombe un cours qui change : le serveur montre en sombre tous les cours
 * de l'enfant, ceux qui changent compris, et la grille barre ceux-là. Le jour et l'heure se
 * comparent, pas la chaîne : la clé d'une case est une heure de début (« Thursday 18:00 »), celle
 * d'un cours peut porter sa fin.
 */
export function changingSlotKeys(
    courses: Pick<UISlotChangeCourse, "dayTime">[],
    grid: Pick<UISlotChangeGrid, "days">
): string[] {
    const hourOf = (key: string) => {
        const parts = weeklySlotParts(null, key)
        return parts == null ? null : `${parts.weekday}|${parts.hours}|${parts.minutes}`
    }
    const changing = new Set(courses.map((course) => hourOf(course.dayTime)).filter((hour) => hour != null))
    return grid.days
        .flatMap((day) => day.slots)
        .filter((slot) => slot.course && changing.has(hourOf(slot.key)))
        .map((slot) => slot.key)
}

/** Un repli choisi pour chaque tuteur qui en demande un ? Rien n'est coché d'avance : la famille choisit. */
export function fallbacksComplete(choice: {
    options: Pick<UISlotChangeOptions, "fallbacks"> | null
    fallbackChoices: Record<string, UISlotChangeFallbackChoice>
}): boolean {
    return (choice.options?.fallbacks ?? []).every((fallback) => choice.fallbackChoices[fallback.key] != null)
}

/** Assez d'horaires cochés pour le serveur ? */
export function enoughSlots(choice: {
    slotKeys: string[]
    options: Pick<UISlotChangeOptions, "minSlots"> | null
}): boolean {
    return choice.options != null && choice.slotKeys.length >= choice.options.minSlots
}

// ── Le rappel d'une étape ──────────────────────────────────────────────────────────────────────

/**
 * Les cours qui changent, nommés en tête d'une étape : « Maths du jeudi 18h00 » ; une même matière
 * se dit une fois (« Maths du lundi 17h00 et du jeudi 18h00 »), une autre suit en minuscule
 * (« Maths du jeudi 18h00 et français du samedi 10h00 »). `withTutor` ajoute le tuteur d'un cours
 * seul ; `minSlots` au-delà de un ajoute ce que la grille demande (« · 2 horaires au moins »).
 */
export function slotChangeCoursesContext(
    courses: Pick<UISlotChangeCourse, "subjects" | "label" | "dayTime" | "tutor">[],
    options: { withTutor?: boolean; minSlots?: number } = {}
): string {
    const { withTutor = false, minSlots = 1 } = options
    let head = ""
    if (courses.length === 1) {
        head = slotChangeCourseLabel(courses[0]!, { withTutor })
    } else {
        const groups: { subjects: string; slots: string[] }[] = []
        for (const course of courses) {
            const subjects = subjectsLabel(course.subjects)
            const slot = formatWeeklySlot(course.label, course.dayTime, "sentence")
            const group = subjects !== "" ? groups.find((candidate) => candidate.subjects === subjects) : undefined
            if (group != null) group.slots.push(slot)
            else groups.push({ subjects, slots: [slot] })
        }
        head = joinFrench(
            groups.map((group, index) => {
                const name = group.subjects !== "" ? group.subjects : "Le cours"
                return `${index === 0 ? name : name.toLowerCase()} ${joinFrench(group.slots.map((slot) => `du ${slot}`))}`
            })
        )
    }
    return minSlots > 1 ? `${head} · ${countLabel(minSlots, "horaire")} au moins` : head
}

// ── Les horaires de chaque offre ───────────────────────────────────────────────────────────────

/**
 * Les horaires cochés que l'offre de ce tuteur portera, dans l'ordre de la semaine : ceux que la
 * grille d'un de SES cours qui changent porte, sauf ceux où il tient déjà un live (`busyKeys`) —
 * la règle de l'offre au serveur. Une case sans cours nommé (un serveur d'avant) sert tout le monde.
 */
export function offeredSlotKeys(
    tutor: Pick<UISlotChangeTutor, "id" | "busyKeys">,
    courses: Pick<UISlotChangeCourse, "liveSessionId" | "tutor">[],
    grid: Pick<UISlotChangeGrid, "days">,
    slotKeys: string[]
): string[] {
    const own = courses
        .filter((course) => String(course.tutor?.id) === String(tutor.id))
        .map((course) => course.liveSessionId)
    return grid.days
        .flatMap((day) => day.slots)
        .filter(
            (slot) =>
                slotKeys.includes(slot.key) &&
                !slot.course &&
                !tutor.busyKeys.includes(slot.key) &&
                (slot.courseLiveSessionIds.length === 0 || slot.courseLiveSessionIds.some((id) => own.includes(id)))
        )
        .map((slot) => slot.key)
}

// ── Les récapitulatifs ─────────────────────────────────────────────────────────────────────────

const SEARCHING = "Un autre tuteur cherché en même temps."
const MAIL_SENT = "Un e-mail de confirmation vous a été envoyé."

/** « son créneau », « ses deux créneaux ». */
function theirSlots(count: number): string {
    return count > 1 ? `ses ${numberInWords(count)} créneaux` : "son créneau"
}

/** Les matières de ces cours, en minuscules — « maths », « maths et physique ». */
function lowerSubjectsOf(courses: Pick<UISlotChangeCourse, "subjects">[]): string {
    const names = [...new Set(courses.map((course) => subjectsLabel(course.subjects).toLowerCase()))]
    return joinFrench(names.filter((name) => name !== ""))
}

/** « le lundi à 18h » — un créneau du serveur (« lundi 18h ») au milieu d'une phrase. */
export function slotAtLabel(label: string): string {
    const match = /^\s*(\S+)\s+(.+?)\s*$/.exec(label)
    return match == null ? label : `le ${match[1]} à ${match[2]}`
}

/**
 * Les semaines sans cours, regroupées dans l'ordre : le serveur en écrit une par séance qu'il crédite
 * (un rattrapage par cours et par semaine), donc deux cours qui laissent la même semaine la disent deux fois.
 */
function creditedWeekCounts(weeks: string[]): { week: string; count: number }[] {
    const counts = new Map<string, number>()
    for (const week of weeks) counts.set(week, (counts.get(week) ?? 0) + 1)
    return [...counts.entries()].map(([week, count]) => ({ week, count }))
}

/**
 * « Confirmer votre choix ? », voie « place » : chaque cours et la place qui le remplace, une ligne
 * par semaine laissée sans un cours (« une séance à rattraper », « deux séances » quand deux cours la
 * laissent, ce que le serveur crédite).
 */
export function seatRecapLines(
    courses: UISlotChangeCourse[],
    seats: UISlotChangeSeat[],
    choices: Record<number, number>
): UIRecapLine[] {
    const chosen = courses.map((course) => ({ course, seat: chosenSeat(seats, choices, course.liveSessionId) }))
    const lines: UIRecapLine[] = chosen.map(({ course, seat }) => {
        if (seat == null) return { tone: "neutral", text: `${slotChangeCourseLabel(course)}.` }
        const from = seat.effectiveOn != null && seat.effectiveOn !== "" ? formatDayLabel(seat.effectiveOn, false) : ""
        return {
            tone: "success",
            lead: `${slotChangeCourseLabel(course)} → ${formatWeeklySlot(seat.label, seat.dayTime, "sentence")}`,
            text: `${seat.tutor.firstname ? `, avec ${seat.tutor.firstname}` : ""}${
                from !== "" ? `, à partir du ${from.toLowerCase()}` : ""
            }.`,
        }
    })
    const weeks = creditedWeekCounts(chosen.flatMap(({ seat }) => seat?.creditedWeeks ?? []))
    return [
        ...lines,
        ...weeks.map(({ week, count }): UIRecapLine => ({
            tone: "success",
            text: `${capitalizeFirst(week)} : ${count > 1 ? `${numberInWords(count)} séances` : "une séance"} à rattraper.`,
        })),
    ]
}

/**
 * « Confirmer votre choix ? », voie « demande » : à qui partent quels horaires, la cohorte qui
 * cherche en même temps, et ce qui se passe si le seul tuteur concerné ne peut pas. Plusieurs
 * matières : chaque offre dit la sienne (« en maths »).
 */
export function offerRecapLines(
    courses: UISlotChangeCourse[],
    options: Pick<UISlotChangeOptions, "grid" | "fallbacks">,
    slotKeys: string[],
    fallbackChoices: Record<string, UISlotChangeFallbackChoice>
): UIRecapLine[] {
    const slots = options.grid.days.flatMap((day) => day.slots)
    const labelOf = (key: string) => slots.find((slot) => slot.key === key)?.label ?? key
    const severalSubjects = new Set(courses.map((course) => lowerSubjectsOf([course]))).size > 1

    const offers: { tutor: UITutor; courses: UISlotChangeCourse[]; keys: string[] }[] = []
    for (const course of courses) {
        if (course.tutor == null) continue
        const offer = offers.find((candidate) => String(candidate.tutor.id) === String(course.tutor?.id))
        if (offer != null) offer.courses.push(course)
        else offers.push({ tutor: course.tutor, courses: [course], keys: [] })
    }
    for (const offer of offers) {
        const known = options.grid.tutors.find((tutor) => String(tutor.id) === String(offer.tutor.id))
        offer.keys = offeredSlotKeys(
            { id: offer.tutor.id, busyKeys: known?.busyKeys ?? [] },
            courses,
            options.grid,
            slotKeys
        )
    }

    const nameOf = (tutor: UITutor) => tutor.firstname || "son tuteur"
    const inSubjects = (offer: { courses: UISlotChangeCourse[] }) => {
        const subjects = lowerSubjectsOf(offer.courses)
        return severalSubjects && subjects !== "" ? ` en ${subjects}` : ""
    }
    const complete = (offer: { courses: unknown[]; keys: string[] }) => offer.keys.length >= offer.courses.length

    const lines: UIRecapLine[] = []
    if (offers.length > 1 && offers.every((offer) => complete(offer) && offer.keys.length === slotKeys.length)) {
        lines.push({
            tone: "warning",
            lead: `Vos ${countLabel(slotKeys.length, "horaire")}`,
            text: ` proposés ${joinFrench(offers.map((offer) => `à ${nameOf(offer.tutor)}${inSubjects(offer)}`))}.`,
        })
    } else {
        for (const offer of offers) {
            const name = nameOf(offer.tutor)
            const count = offer.courses.length
            if (!complete(offer)) {
                lines.push({
                    tone: "neutral",
                    text: `Ces horaires ne sont pas libres chez ${name}${inSubjects(offer)}.`,
                })
            } else if (count > 1) {
                const subjects = lowerSubjectsOf(offer.courses)
                const pool = offer.keys.length
                lines.push({
                    tone: "warning",
                    lead: `Ses ${numberInWords(count)} cours${subjects !== "" ? ` ${elide(subjects)}` : ""}`,
                    text: ` proposés ensemble à ${name}, sur ${
                        pool === count ? `vos ${countLabel(pool, "horaire")}` : `${count} de vos ${pool} horaires`
                    }.`,
                })
            } else {
                lines.push({
                    tone: "warning",
                    lead: capitalizeFirst(joinFrench(offer.keys.map(labelOf))),
                    text: ` ${offer.keys.length > 1 ? "proposés" : "proposé"} à ${name}${inSubjects(offer)}.`,
                })
            }
        }
    }
    lines.push({ tone: "neutral", text: SEARCHING })

    // Un seul tuteur : ce qui se passe s'il ne peut pas se dit ici, demandé à l'étape d'avant ou non.
    if (offers.length === 1) {
        const name = nameOf(offers[0]!.tutor)
        const ofSubject = (subjects: UISubject[]) => {
            const label = subjectsLabel(subjects).toLowerCase()
            return severalSubjects && label !== "" ? ` ${elide(label)}` : ""
        }
        for (const fallback of options.fallbacks) {
            const choice = fallbackChoices[fallback.key]
            if (choice == null) continue
            lines.push({
                tone: "neutral",
                text:
                    choice === "keep_tutor"
                        ? `Si ${name} ne peut pas : ${name} garde ${fallback.keptLabel || "son autre créneau"}, et un autre tuteur pour le nouvel horaire.`
                        : `Si ${name} ne peut pas : un autre tuteur pour ${theirSlots(fallbackSlotsCount(fallback, courses))}${ofSubject(fallback.subjects)}.`,
            })
            // « Un autre tuteur pour les deux » : l'heure qui reste s'arrête à son jour, et ne revient que s'il accepte.
            if (choice !== "keep_tutor") {
                for (const stop of fallback.keptStops) {
                    lines.push({
                        tone: "neutral",
                        text: `${capitalizeFirst(stop.label)} s'arrête aussi à partir du ${stop.stopsOnLabel}, en attendant la réponse de ${name}.`,
                    })
                }
            }
        }
        for (const row of tutorsWithoutFallback(options.fallbacks, courses)) {
            lines.push({
                tone: "neutral",
                text: `Si ${name} ne peut pas : un autre tuteur pour ${theirSlots(row.count)}${ofSubject(row.subjects)}.`,
            })
        }
    }
    return lines
}

/**
 * « Camille est prévenue. », « Yanis est prévenu. », « Camille et Léa sont prévenues. » — accordé au
 * genre que le serveur connaît ; sans lui, la phrase ne s'accorde pas : « Nous avons prévenu Camille. »
 */
export function tutorsWarned(tutors: { name: string; gender?: "homme" | "femme" }[]): string | null {
    const unique = tutors
        .filter((tutor) => tutor.name !== "")
        .filter((tutor, index, all) => all.findIndex((other) => other.name === tutor.name) === index)
    if (unique.length === 0) return null
    const names = joinFrench(unique.map((tutor) => tutor.name))
    if (unique.some((tutor) => tutor.gender == null)) return `Nous avons prévenu ${names}.`
    const feminine = unique.every((tutor) => tutor.gender === "femme")
    if (unique.length === 1) return `${names} est ${feminine ? "prévenue" : "prévenu"}.`
    return `${names} sont ${feminine ? "prévenues" : "prévenus"}.`
}

/** L'accusé : ce que le serveur a fait, ligne par ligne. */
/** Le message préparé de « Pour changer de tuteur, écrivez-nous » : la famille n'a plus qu'à dire pourquoi. */
export function tutorChangeRequestMessage(childFirstname?: string | null): string {
    return `Bonjour, je souhaite changer de tuteur${childFirstname ? ` pour ${childFirstname}` : ""}. `
}

/**
 * Ce qui s'arrête, dit avant de confirmer et redit dans l'accusé : « Le cours de jeudi 18h s'arrête à partir du
 * 5 novembre et sa place pourra être donnée à un autre élève. » `beforeConfirm` le dit comme la conséquence du
 * geste, dans la question : « En confirmant, le cours de jeudi 18h s'arrête… ». `null` sans cours.
 */
export function slotChangeStopWarning(
    courses: UISlotChangeCourse[],
    options: { beforeConfirm?: boolean } = {}
): string | null {
    if (courses.length === 0) return null
    const sentence = (text: string) => (options.beforeConfirm ? `En confirmant, ${text}` : capitalizeFirst(text))
    const fromOf = (course: UISlotChangeCourse) =>
        course.effectiveOn ? `à partir du ${formatDayMonth(course.effectiveOn, false)}` : null
    if (courses.length === 1) {
        const from = fromOf(courses[0]!)
        return sentence(
            `le cours de ${courses[0]!.label} s'arrête${from ? ` ${from}` : ""} et sa place pourra être donnée à un autre élève.`
        )
    }
    const froms = courses.map(fromOf)
    const sameFrom = froms.every((from) => from === froms[0]) ? froms[0] : undefined
    const labels =
        sameFrom !== undefined
            ? joinFrench(courses.map((course) => course.label))
            : joinFrench(courses.map((course, i) => (froms[i] ? `${course.label} (${froms[i]})` : course.label)))
    return sentence(
        `les cours de ${labels} s'arrêtent${sameFrom ? ` ${sameFrom}` : ""} et leurs places pourront être données à d'autres élèves.`
    )
}

export function slotChangeOutcomeLines(outcome: UISlotChangeOutcome, childFirstname: string): UIRecapLine[] {
    const lines: UIRecapLine[] = []
    if (outcome.kind === "seat") {
        for (const change of outcome.changes) {
            const from =
                change.effectiveOn != null && change.effectiveOn !== ""
                    ? formatDayMonth(change.effectiveOn, false)
                    : change.effective
            lines.push({
                tone: "success",
                lead: `${childFirstname} garde ${change.tutor || "son tuteur"} ${slotAtLabel(change.to)}`,
                text: from !== "" ? `, à partir du ${from}.` : ".",
            })
        }
        if (outcome.creditedWeeks.length > 0) {
            for (const { week, count } of creditedWeekCounts(outcome.creditedWeeks)) {
                lines.push({
                    tone: "success",
                    text:
                        count > 1
                            ? `${capitalizeFirst(numberInWords(count))} séances à rattraper sont ajoutées pour la ${week}.`
                            : `Une séance à rattraper est ajoutée pour la ${week}.`,
                })
            }
        } else if (outcome.credits > 0) {
            lines.push({
                tone: "success",
                text:
                    outcome.credits > 1
                        ? `${outcome.credits} séances à rattraper sont ajoutées pour les semaines sans cours.`
                        : "Une séance à rattraper est ajoutée pour la semaine sans cours.",
            })
        }
        const warned = outcome.tutorsNotified
            ? tutorsWarned(outcome.changes.map((change) => ({ name: change.tutor, gender: change.tutorGender })))
            : null
        if (warned != null) lines.push({ tone: "neutral", text: warned })
    } else {
        for (const offer of outcome.offers) {
            const several = offer.slotLabels.length > 1
            lines.push({
                tone: "warning",
                lead: `${capitalizeFirst(joinFrench(offer.slotLabels))} ${several ? "sont proposés" : "est proposé"} à ${
                    offer.tutor.firstname || "son tuteur"
                }.`,
            })
        }
        lines.push({ tone: "neutral", text: "Nous cherchons aussi un autre tuteur." })
        for (const held of outcome.held) {
            const tutor = held.tutor?.firstname ? ` avec ${held.tutor.firstname}` : ""
            lines.push({
                tone: "neutral",
                text:
                    held.stopsOnLabel != null
                        ? `${capitalizeFirst(held.label)} : s'arrête à partir du ${held.stopsOnLabel}, et revient si ${
                              held.tutor?.firstname || "son tuteur"
                          } accepte.`
                        : `${capitalizeFirst(held.label)} : sa place${tutor} est gardée.`,
            })
        }
    }
    if (outcome.mailSent) lines.push({ tone: "neutral", text: MAIL_SENT })
    return lines
}

/**
 * La demande « Modifier mes créneaux » qui attend son issue, dite de haut pour « Demandes en cours » : le cours
 * qui change (« Jeudi 18h avec Camille ») et où elle en est ; les dates et les horaires proposés restent au détail.
 */
export function slotChangeRequest(pending: UISlotChangePending): UIParentRequest {
    return {
        id: "slot_change",
        kind: "slot_change",
        title: "Changement de créneau",
        detail: capitalizeFirst(joinFrench(pending.courses.map((course) => course.label))),
        statusLine: pending.statusLine,
    }
}
